import type { MeResponse } from '../api/admin';
import { visibleSettingsPages } from './settingsNav';
import { platformPalettePages } from './platformNav';

/**
 * The ⌘K index (docs/PLAYBOOK-redesign.md §4, R1): every page a person can
 * reach from the shell, the current form's tabs, the forms themselves, and
 * the shell's own actions — built on the client from what /admin/me already
 * says. No server search: responses are not in here (v1).
 *
 * The settings pages come from the same list the settings sidebar draws
 * (shared/settingsNav.ts, R10), so the palette never offers a page that
 * would 402 or 403 on arrival. The console's pages likewise come from the
 * platform sidebar's list (shared/platformNav.ts, R12).
 */
export interface Command {
  id: string;
  /** The small tag before the label: what kind of thing this jumps to. */
  group: 'Action' | 'Page' | 'Settings' | 'Platform' | 'This form' | 'Form' | 'Site';
  label: string;
  /** Quiet text after the label, e.g. a form's URL. */
  hint?: string;
  /** Extra words the search matches on. */
  keywords?: string;
  /** Where it goes. */
  to?: string;
  /** What it does instead of navigating. */
  run?: () => void;
  /** Styles the row as the dangerous one (Log out). */
  danger?: boolean;
}

export interface ShellFacts {
  me: Pick<MeResponse, 'admin' | 'account' | 'features' | 'masquerading' | 'isPlatformAdmin'>;
  pathname: string;
  onChangePassword: () => void;
  onLogout: () => void;
  onExitSupport?: () => void;
}

/** The pages a form carries, in the form bar's order and with its labels (R3). */
export const FORM_TABS: readonly [string, string][] = [
  ['build', 'Fields'], ['logic', 'Logic'], ['document', 'Document'],
  ['workflow', 'Workflow'],
  ['runs', 'Runs'],
  ['access', 'Link & access'], ['invitations', 'Invitations'],
  ['submissions', 'Responses'], ['summary', 'Summary'], ['analytics', 'Analytics'],
  ['settings', 'Form settings'], ['versions', 'Versions'],
];

/** The settings pages this person can open, in the sidebar's order; the plan picker rides beside Billing. */
export function settingsPages(me: ShellFacts['me']): [string, string, string?][] {
  const pages: [string, string, string?][] = [];
  for (const p of visibleSettingsPages(me)) {
    pages.push([p.to, p.label, p.keywords]);
    if (p.to === '/admin/settings/billing') pages.push(['/admin/settings/billing/plans', 'Change plan', 'upgrade downgrade plans']);
  }
  return pages;
}

/** Everything the palette offers before the forms list arrives. */
export function shellCommands(f: ShellFacts): Command[] {
  const out: Command[] = [];
  const { me, pathname } = f;
  const inAccount = !!me.account;
  const staff = !!me.isPlatformAdmin;

  if (inAccount) {
    out.push({ id: 'page:forms', group: 'Page', label: 'Forms', keywords: 'all forms home list', to: '/admin' });
    out.push({ id: 'page:settings', group: 'Page', label: 'Settings', keywords: 'account settings', to: '/admin/settings' });
    for (const [to, label, words] of settingsPages(me)) {
      out.push({ id: `settings:${to}`, group: 'Settings', label, keywords: `settings account${words ? ` ${words}` : ''}`, to });
    }
    const m = /^\/admin\/forms\/(\d+)(?:\/|$)/.exec(pathname);
    if (m) {
      for (const [seg, label] of FORM_TABS) {
        out.push({ id: `form-tab:${seg}`, group: 'This form', label, keywords: 'tab form', to: `/admin/forms/${m[1]}/${seg}` });
      }
    }
  }
  if (staff) {
    for (const { to, label, keywords } of platformPalettePages()) {
      out.push({ id: `platform:${to}`, group: 'Platform', label, keywords: `platform console staff${keywords ? ` ${keywords}` : ''}`, to });
    }
  }
  out.push({ id: 'site:home', group: 'Site', label: 'Marketing site', keywords: 'home public website', to: '/' });
  out.push({ id: 'site:pricing', group: 'Site', label: 'Pricing', keywords: 'plans public website', to: '/pricing' });

  if (inAccount) {
    out.push({ id: 'action:profile', group: 'Action', label: 'Profile & security', keywords: 'two-factor 2fa password sessions me', to: '/admin/settings/profile' });
  }
  out.push({ id: 'action:password', group: 'Action', label: 'Change password', keywords: 'security', run: f.onChangePassword });
  if (me.masquerading && f.onExitSupport) {
    out.push({ id: 'action:exit-support', group: 'Action', label: 'Exit to platform', keywords: 'support mode leave account', run: f.onExitSupport });
  }
  out.push({ id: 'action:logout', group: 'Action', label: 'Log out', keywords: 'sign out logout', run: f.onLogout, danger: true });
  return out;
}

/** Forms, as commands: the label is the name, the hint its address. */
export function formCommands(forms: { id: number; name: string; slug: string }[], accountSlug?: string | null): Command[] {
  return forms.map((form) => ({
    id: `form:${form.id}`, group: 'Form', label: form.name,
    hint: accountSlug ? `/f/${accountSlug}/${form.slug}` : `/f/${form.slug}`,
    keywords: form.slug, to: `/admin/forms/${form.id}/build`,
  }));
}

/** Every typed word must appear in the label, hint or keywords. */
export function matchCommands(all: Command[], query: string, limit = 40): Command[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return all.slice(0, limit);
  const hay = (c: Command) => `${c.label} ${c.hint || ''} ${c.keywords || ''} ${c.group}`.toLowerCase();
  const scored = all
    .map((c) => {
      const h = hay(c);
      if (!words.every((w) => h.includes(w))) return null;
      const label = c.label.toLowerCase();
      // A label that starts with the query beats one that merely contains it.
      const score = label.startsWith(words[0]) ? 0 : label.includes(words[0]) ? 1 : 2;
      return { c, score };
    })
    .filter((x): x is { c: Command; score: number } => !!x)
    .sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).map((x) => x.c);
}

/** Is the key event's target somewhere a person types? Then a shortcut stays out of it. */
export function inEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return !!el.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]');
}
